import Indicator from "@/components/shared/Indicator";
import { LoadingMask } from "@/components/shared/LoadingSection";
import PageHeader from "@/components/shared/PageHeader";
import OrderConfirmDialog from "@/components/tms/order/create/OrderConfirm";
import OrderProducts from "@/components/tms/order/create/OrderProducts";
import { usePageQuery } from "@/hooks/shared/query";
import {
  Cube2COrder,
  DialogRef,
  IntegrationOrderType,
  TMSCustomerVo,
  TMSOrderVCInfo,
  TMSScanOrderConfig,
  TMSScanOrderCreateDo,
} from "@/types";
import { Alert, Button, Descriptions, FormInstance, Input } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useIntegrationOrder,
  useCubeOrderDetail,
  PageQueryConfig,
  useIntegrationOrderProductUsage,
} from "./helper";
import VCInfoForm from "@/components/tms/order/create/VCInfoForm";
import CustomerSelect from "@/components/tms/order/create/Customer";
import AddressSelect from "@/components/tms/address/Select";
import { useGlobalContext } from "@/hooks/context";
import ScanConfig from "@/components/tms/order/create/ScanConfig";
import { wrapPage } from "@/utils/pages";
import classNames from "classnames";

type PageQueryParamsType = {
  cubeOrderId: string;
  type: string;
};

export function CreateScanOrderPageFC() {
  const { hintPrompt } = useGlobalContext();
  const [query, setQueryParam] = usePageQuery<PageQueryParamsType>(
    [],
    PageQueryConfig,
  );

  const orderType = query.type as IntegrationOrderType;
  const { data: integrationOrder, isFetching: isIntegrationOrderFetching } =
    useIntegrationOrder(query.cubeOrderId);

  const { data: cubeOrderDetail, isFetching: isCubeOrderDetailFetching } =
    useCubeOrderDetail(integrationOrder?.customerNumber, orderType);

  const { data: tmsProductUsage, isFetching: isFetchingTMSProductUsage } =
    useIntegrationOrderProductUsage(query.cubeOrderId);

  // for customer
  const [customer, setCustomer] = useState<TMSCustomerVo | undefined>(
    undefined,
  );
  const [addressId, setAddressId] = useState<string | undefined>(undefined);
  const vcInfoFormRef = useRef<FormInstance<TMSOrderVCInfo>>(null);
  const scanConfigFormRef = useRef<FormInstance<TMSScanOrderConfig>>(null);
  const [requireVCInfo, setRequireVCInfo] = useState<boolean>(false);

  const loading = useMemo(
    () =>
      isIntegrationOrderFetching ||
      isCubeOrderDetailFetching ||
      isFetchingTMSProductUsage,
    [
      isIntegrationOrderFetching,
      isCubeOrderDetailFetching,
      isFetchingTMSProductUsage,
    ],
  );

  const orderConfirmDialogRef = useRef<DialogRef>(null);

  const orderError = useMemo(() => {
    if (loading) return null;
    if (integrationOrder && cubeOrderDetail) return null;
    return "Failed to load order!";
  }, [integrationOrder, cubeOrderDetail, loading]);

  const pageHeaderText = useMemo(() => {
    if (loading) return "Loading...";
    if (integrationOrder && cubeOrderDetail) {
      if (integrationOrder.type === IntegrationOrderType.Customer) {
        return `Order ${integrationOrder.customerNumber} 
        (${(cubeOrderDetail as Cube2COrder).instruction.instructionNumber})`;
      } else if (integrationOrder.type === IntegrationOrderType.Business) {
        return `Order ${integrationOrder.customerNumber} 
        (${integrationOrder.cubeDeliveryNo})`;
      }
    }
    return "Failed to load order!";
  }, [integrationOrder, cubeOrderDetail, loading]);

  const infoItems = useMemo(() => {
    if (loading) return [];
    if (orderError) return [];
    const orderType = integrationOrder?.type;
    return [
      {
        label: "Customer Number",
        children: integrationOrder?.customerNumber,
      },
      {
        label: "Cube No.",
        children:
          orderType === IntegrationOrderType.Customer
            ? (cubeOrderDetail as Cube2COrder).instruction.instructionNumber
            : integrationOrder?.cubeDeliveryNo,
      },
      {
        label: "Order Type",
        children: integrationOrder?.type,
      },
      {
        label: "Customer",
        children: integrationOrder?.customerName,
      },
      {
        label: "Address",
        children: (
          <div>
            <span>{integrationOrder?.address?.street} </span>
            <span>
              {integrationOrder?.address?.city},{" "}
              {integrationOrder?.address?.state}{" "}
              {integrationOrder?.address?.zip}
            </span>
          </div>
        ),
      },
    ];
  }, [integrationOrder, cubeOrderDetail, loading, orderError]);
  const [productCartons, setProductCartons] = useState<Record<string, number>>(
    {},
  );

  const changeCartons = useCallback((productNo: string, cartons: number) => {
    setProductCartons((prev) => ({
      ...prev,
      [productNo]: Math.max(0, cartons),
    }));
  }, []);

  const resetCartons = useCallback(() => {
    setProductCartons(
      Object.fromEntries(
        integrationOrder?.products.map((product) => {
          const used = tmsProductUsage[product.productNo] ?? 0;
          return [product.productNo, product.quantity - used];
        }) ?? [],
      ),
    );
  }, [integrationOrder?.products, tmsProductUsage]);

  const handleCreateClick = useCallback(async () => {
    if (scanConfigFormRef.current == null) {
      hintPrompt(
        "error",
        "Scan Config is required",
        "Please fill in the Scan Config",
      );
      return Promise.reject(new Error("Scan Config is required"));
    }
    if (!customer) {
      hintPrompt("error", "Customer is required", "Please select a customer");
      return Promise.reject(new Error("Customer is required"));
    }
    if (!addressId) {
      hintPrompt("error", "Address is required", "Please select an address");
      return Promise.reject(new Error("Address is required"));
    }

    const scanConfig = await scanConfigFormRef.current
      ?.validateFields()
      .catch((error) => {
        hintPrompt(
          "error",
          "Failed to validate Scan Config",
          error?.message ?? "Unknown error!",
        );
      });
    if (scanConfig == null) {
      hintPrompt(
        "error",
        "Failed to validate Scan Config",
        "Something happened",
      );
      return Promise.reject(new Error("Failed to validate Scan Config"));
    }

    if (scanConfig.isSSCCOrder === true && customer.sscc == null) {
      hintPrompt(
        "error",
        "Customer SSCC is required",
        "Please fill in the Customer SSCC",
      );
      return Promise.reject(
        new Error("Customer SSCC is required if Pallet Label SSCC is enabled"),
      );
    }

    for (const productNo of Object.keys(productCartons)) {
      const originalProduct = integrationOrder?.products.find(
        (product) => product.productNo === productNo,
      );
      if (originalProduct == null) {
        hintPrompt(
          "error",
          "Product not found",
          `Product ${productNo} not found`,
        );
        return Promise.reject(new Error("Product not found"));
      }
      const cartons = productCartons[productNo];
      if (originalProduct.quantity < cartons) {
        hintPrompt(
          "error",
          "Invalid carton quantity",
          `${productNo} has ${cartons} cartons, larger than quantity ${originalProduct.quantity}`,
        );
        return Promise.reject(new Error("Invalid carton quantity"));
      }
      if (
        originalProduct.quantity / cartons !==
        Math.floor(originalProduct.quantity / cartons)
      ) {
        hintPrompt(
          "error",
          "Invalid carton quantity",
          `${productNo} has ${originalProduct.quantity} quantity, but ${cartons} cartons is not divisible by quantity`,
        );
        return Promise.reject(new Error("Invalid carton quantity"));
      }
    }

    const cubeOrderNo =
      orderType === IntegrationOrderType.Customer
        ? (cubeOrderDetail as Cube2COrder).instruction.instructionNumber
        : integrationOrder!.cubeDeliveryNo;

    const orderDo = {
      customerId: customer.id,
      addressId: addressId,
      integrationOrderId: integrationOrder!.id,
      cubeOrderNo,
      vcInfo: undefined,
      scanConfig: scanConfig,
      useAllProducts: true,
    } as Omit<TMSScanOrderCreateDo, "productCartons">;

    if (requireVCInfo) {
      if (vcInfoFormRef.current == null) {
        hintPrompt(
          "error",
          "VC Info is required",
          "Please fill in the VC Info",
        );
        return Promise.reject(new Error("VC Info is required"));
      }
      const vcInfo = await vcInfoFormRef.current
        .validateFields()
        .catch((error) => {
          hintPrompt(
            "error",
            "Failed to validate VC Info",
            error?.message ?? "Unknown error!",
          );
          return Promise.reject(new Error("Failed to validate VC Info"));
        });
      if (vcInfo == null) {
        hintPrompt("error", "Failed to validate VC Info", "Something happened");
        return Promise.reject(new Error("Failed to validate VC Info"));
      }
      orderDo.vcInfo = vcInfo;
    }

    orderConfirmDialogRef.current?.showModal({
      orderDo,
      order: integrationOrder,
      cubeOrderNo,
      productCartons: productCartons,
    });
    return Promise.resolve(true);
  }, [
    requireVCInfo,
    addressId,
    customer,
    orderType,
    cubeOrderDetail,
    integrationOrder,
    productCartons,
    orderConfirmDialogRef,
    hintPrompt,
  ]);

  useEffect(() => {
    const productCartons = Object.fromEntries(
      integrationOrder?.products.map((product) => {
        const used = tmsProductUsage[product.productNo] ?? 0;
        return [product.productNo, product.quantity - used];
      }) ?? [],
    );
    setProductCartons(productCartons);
  }, [integrationOrder, tmsProductUsage]);

  if (!query.cubeOrderId) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <Input
          autoFocus
          size="large"
          placeholder="Scan to input order ID"
          className="max-w-md"
          allowClear
          onPressEnter={(e) => {
            const value = (e.target as HTMLInputElement).value.trim();
            if (value) {
              setQueryParam("cubeOrderId", value);
            }
          }}
        />
      </div>
    );
  }

  if (orderError) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader
          title="Something went wrong!"
          description=""
          className="shrink-0 pt-2 pl-2 pb-3"
        />
        <Alert message="Error" description={orderError} type="error" showIcon />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader
          title="Loading..."
          description=""
          className="shrink-0 pt-2 pl-2 pb-3"
        />
        <LoadingMask info="Loading..." className="bg-gray-100/50" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PageHeader
        title={pageHeaderText}
        description="scroll down to view more content"
        className="shrink-0 pt-2 pl-2 pb-3"
      />
      <div
        className={classNames(
          "flex flex-grow flex-col rounded-md",
          "overflow-y-auto global__no-scrollbar",
        )}
      >
        <div>
          <Indicator label="Basic Info" />
          {infoItems.length > 0 && (
            <Descriptions
              column={2}
              size="small"
              bordered
              items={infoItems}
              styles={{
                label: { width: "16%" },
                content: { width: "36%" },
              }}
            />
          )}
        </div>
        <div>
          <Indicator label="Customer" />
          <CustomerSelect
            value={customer?.name}
            selectClassName="min-w-100"
            defaultValue={integrationOrder?.customerName}
            onChange={setCustomer}
            disabled={isIntegrationOrderFetching}
          />
        </div>
        <div>
          <Indicator label="Address" />
          <AddressSelect
            value={addressId}
            selectClassName="min-w-160"
            onChange={setAddressId}
            disabled={isIntegrationOrderFetching}
          />
        </div>
        <ScanConfig
          ref={scanConfigFormRef}
          onFieldsChange={(values) => {
            setRequireVCInfo(values.isSSCCOrder);
          }}
        />
        {requireVCInfo && <VCInfoForm ref={vcInfoFormRef} />}
        <div className="flex flex-grow flex-col">
          <Indicator label="Products" />
          <OrderProducts
            products={integrationOrder!.products}
            productUsage={tmsProductUsage}
            className="!min-h-[540px] border-[1px] !rounded-md bg-white border-gray-200"
            changeCartons={changeCartons}
            productCartons={productCartons}
          />
        </div>
        <div className="flex flex-row gap-2 pt-2 justify-end">
          <Button onClick={resetCartons}>Reset Cartons</Button>
          <Button>Cancel</Button>
          <Button type="primary" onClick={handleCreateClick}>
            Create Order
          </Button>
        </div>
      </div>
      <OrderConfirmDialog ref={orderConfirmDialogRef} />
    </div>
  );
}

export const CreateScanOrderPage = wrapPage(CreateScanOrderPageFC);
